// =============================================================================
// MeuDriver — Batch P3: AdminAnnouncements
// CRUD de anúncios globais da plataforma.
// =============================================================================

import { useState } from 'react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Plus, Megaphone, Pencil, Power, PowerOff, Loader2, Info, AlertTriangle, AlertOctagon } from 'lucide-react'
import { useForm, type SubmitHandler } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

import {
  useAdminAnnouncements,
  useCreateAnnouncement,
  useUpdateAnnouncement,
  type Announcement,
  type CreateAnnouncementInput,
  type AnnouncementAudience,
  type AnnouncementSeverity,
} from '@/hooks/useAnnouncements'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Form schema
// ---------------------------------------------------------------------------

const schema = z.object({
  title:           z.string().min(3).max(200),
  body:            z.string().min(5).max(2000, 'Máximo 2000 caracteres'),
  target_audience: z.enum(['all', 'students', 'instructors']),
  severity:        z.enum(['info', 'warning', 'critical']),
  starts_at:       z.string().optional(),
  ends_at:         z.string().nullable().optional(),
  is_active:       z.boolean().optional(),
})

type FormValues = z.infer<typeof schema>

// ---------------------------------------------------------------------------
// Helpers de label
// ---------------------------------------------------------------------------

const AUDIENCE_LABEL: Record<AnnouncementAudience, string> = {
  all:         'Todos',
  students:    'Alunos',
  instructors: 'Instrutores',
}

const SEVERITY_LABEL: Record<AnnouncementSeverity, string> = {
  info:     'Informação',
  warning:  'Atenção',
  critical: 'Crítico',
}

const SEVERITY_ICON: Record<AnnouncementSeverity, typeof Info> = {
  info:     Info,
  warning:  AlertTriangle,
  critical: AlertOctagon,
}

const SEVERITY_COLOR: Record<AnnouncementSeverity, string> = {
  info:     'text-blue-500',
  warning:  'text-yellow-500',
  critical: 'text-red-500',
}

// ---------------------------------------------------------------------------
// Preview inline
// ---------------------------------------------------------------------------

function AnnouncementPreview({ values }: { values: Partial<FormValues> }) {
  const severity = (values.severity ?? 'info') as AnnouncementSeverity
  const Icon = SEVERITY_ICON[severity]
  const colorClass = SEVERITY_COLOR[severity]

  const bgClass = {
    info:     'bg-blue-50 border-blue-200',
    warning:  'bg-yellow-50 border-yellow-200',
    critical: 'bg-red-50 border-red-200',
  }[severity]

  return (
    <div className={cn('rounded-lg border px-4 py-3 flex items-start gap-3', bgClass)}>
      <Icon className={cn('h-4 w-4 mt-0.5 shrink-0', colorClass)} />
      <div>
        <p className="text-sm font-semibold text-gray-800">{values.title || 'Título do anúncio'}</p>
        <p className="text-xs text-gray-600 mt-0.5">{values.body || 'Conteúdo do anúncio aparece aqui.'}</p>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

interface AnnouncementModalProps {
  open: boolean
  onClose: () => void
  editing: Announcement | null
}

function AnnouncementModal({ open, onClose, editing }: AnnouncementModalProps) {
  const create = useCreateAnnouncement()
  const update = useUpdateAnnouncement()
  const isPending = create.isPending || update.isPending

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: editing
      ? {
          title:           editing.title,
          body:            editing.body,
          target_audience: editing.target_audience,
          severity:        editing.severity,
          starts_at:       editing.starts_at?.slice(0, 16),
          ends_at:         editing.ends_at?.slice(0, 16) ?? '',
          is_active:       editing.is_active,
        }
      : {
          target_audience: 'all',
          severity:        'info',
          is_active:       true,
        },
  })

  const values = watch()

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    const payload: CreateAnnouncementInput = {
      title:           data.title,
      body:            data.body,
      target_audience: data.target_audience,
      severity:        data.severity,
      is_active:       data.is_active ?? true,
      starts_at:       data.starts_at ? new Date(data.starts_at).toISOString() : undefined,
      ends_at:         data.ends_at ? new Date(data.ends_at).toISOString() : null,
    }

    const done = () => { reset(); onClose() }

    if (editing) {
      update.mutate({ id: editing.id, ...payload }, { onSuccess: done })
    } else {
      create.mutate(payload, { onSuccess: done })
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? 'Editar anúncio' : 'Novo anúncio'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label>Título</Label>
            <Input {...register('title')} placeholder="Ex: Manutenção programada amanhã" data-testid="ann-title" />
            {errors.title && <p className="text-xs text-red-500">{errors.title.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label>Conteúdo</Label>
            <Textarea
              {...register('body')}
              placeholder="Descreva o anúncio em detalhes..."
              rows={3}
              maxLength={2000}
              data-testid="ann-body"
            />
            {errors.body && <p className="text-xs text-red-500">{errors.body.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Público-alvo</Label>
              <Select
                defaultValue={editing?.target_audience ?? 'all'}
                onValueChange={(v) => setValue('target_audience', v as AnnouncementAudience)}
              >
                <SelectTrigger data-testid="ann-audience">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="students">Alunos</SelectItem>
                  <SelectItem value="instructors">Instrutores</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Severidade</Label>
              <Select
                defaultValue={editing?.severity ?? 'info'}
                onValueChange={(v) => setValue('severity', v as AnnouncementSeverity)}
              >
                <SelectTrigger data-testid="ann-severity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">Informação</SelectItem>
                  <SelectItem value="warning">Atenção</SelectItem>
                  <SelectItem value="critical">Crítico</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Início</Label>
              <Input type="datetime-local" {...register('starts_at')} />
            </div>
            <div className="space-y-1.5">
              <Label>Fim (vazio = sem fim)</Label>
              <Input type="datetime-local" {...register('ends_at')} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Preview</Label>
            <AnnouncementPreview values={values} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending} data-testid="ann-save-btn">
              {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? 'Salvar' : 'Publicar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Página principal
// ---------------------------------------------------------------------------

export default function AdminAnnouncements() {
  const { data: announcements = [], isLoading } = useAdminAnnouncements()
  const update = useUpdateAnnouncement()

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing]     = useState<Announcement | null>(null)

  const openCreate = () => { setEditing(null); setModalOpen(true) }
  const openEdit   = (a: Announcement) => { setEditing(a); setModalOpen(true) }

  const toggleActive = (a: Announcement) => {
    update.mutate({ id: a.id, is_active: !a.is_active })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Anúncios da Plataforma</h1>
          <p className="text-sm text-gray-500 mt-1">Banners e notificações globais exibidos para os usuários.</p>
        </div>
        <Button onClick={openCreate} className="gap-2" data-testid="create-announcement-btn">
          <Plus className="h-4 w-4" />
          Novo anúncio
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Megaphone className="h-4 w-4 text-brand-600" />
            Todos os anúncios
            {!isLoading && (
              <Badge variant="secondary" className="ml-auto">{announcements.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : announcements.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Megaphone className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Nenhum anúncio criado ainda.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50 dark:bg-gray-900/50">
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Título</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Público</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Severidade</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Início / Fim</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">Status</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {announcements.map((ann) => {
                    const Icon = SEVERITY_ICON[ann.severity] ?? Info
                    return (
                      <tr key={ann.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                        <td className="px-4 py-3 max-w-xs">
                          <p className="font-medium text-gray-900 dark:text-white truncate">{ann.title}</p>
                          <p className="text-xs text-gray-500 truncate">{ann.body}</p>
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                          {AUDIENCE_LABEL[ann.target_audience]}
                        </td>
                        <td className="px-4 py-3">
                          <span className={cn('flex items-center gap-1', SEVERITY_COLOR[ann.severity])}>
                            <Icon className="h-3.5 w-3.5" />
                            {SEVERITY_LABEL[ann.severity]}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">
                          <span>{format(new Date(ann.starts_at), 'dd/MM/yy', { locale: ptBR })}</span>
                          {ann.ends_at && (
                            <span className="ml-1">
                              → {format(new Date(ann.ends_at), 'dd/MM/yy', { locale: ptBR })}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={ann.is_active ? 'default' : 'secondary'}>
                            {ann.is_active ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => openEdit(ann)}
                              data-testid={`edit-ann-${ann.id}`}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => toggleActive(ann)}
                              title={ann.is_active ? 'Desativar' : 'Ativar'}
                              data-testid={`toggle-ann-${ann.id}`}
                            >
                              {ann.is_active
                                ? <PowerOff className="h-3.5 w-3.5 text-red-500" />
                                : <Power className="h-3.5 w-3.5 text-green-500" />}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <AnnouncementModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        editing={editing}
      />
    </div>
  )
}
